import React, { useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { FetchStatus, FetchType } from '../../../enums';
import { IPeopleHeaderActionsCMSLayoutProps } from './freemium-people.cms.models';
import { BulkActionsMenu } from './bulk-actions-menu/freemium-bulk-actions-menu';
import { AddTwoToneIcon, Button, Checkbox, DropdownOption, IconButton, Modal, Searchfield, Tooltip, useMatchScreenWidth } from '@keplerco/core';
import styles from './freemium-people.module.css';

import { FreemiumBulkActionModalPayload } from './bulk-actions-menu/freemium-bulk-actions-menu.models';
import { ConfirmationModalLayout } from '../../../widgets/layouts';
import { UploadTwoToneIcon } from '../../user-management/people/assets/upload-two-tone.icon';


export interface Entity {
  name: string;
  slug: string;
}
export function PeopleHeaderActionsCMSLayout(props: IPeopleHeaderActionsCMSLayoutProps): JSX.Element {
  const { fetchState } = useAppState();
  const actions = useAppActions();

  const [bulkActionModalState, setBulkActionModalState] = useState<FreemiumBulkActionModalPayload>({
    selectedPeople: undefined,
    showModal: false,
  });

  const showIconButton = useMatchScreenWidth(1360);
  async function onInputHandler(value: string) {
    actions.startLoader({ path: props.path, type: FetchType.Custom });
    props.setRequest({ ...props.request, search: value, page: 1 });
  }

  if (props.isMobile)
    return (
      <React.Fragment>
        <div className={styles.peopleActions}>

          <div>
            <Tooltip content="Bulk Upload">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => props.setBulkUploadVisible({ show: true, data: undefined })}>
                <UploadTwoToneIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div>
            <Tooltip content="Add A Person">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => props.setPersonToManage({ show: true })}>
                <AddTwoToneIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div style={{ width: '100%' }}>
            <Searchfield loading={fetchState[props.path].status === FetchStatus.Active && fetchState[props.path].type === FetchType.Custom} responsive onInput={onInputHandler} />
          </div>
        </div>

        <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 15 }}>
          <Checkbox
            checked={props.selectedRows.length === props.data?.employees.length}
            id="selectAll"
            onChange={() => {
              if (!props.data?.employees) return;
              const ids: any[] = props.data.employees.map(person => ({ id: person.id! }));
              props.setSelectedRows(props.selectedRows.length === props.data?.employees.length ? [] : ids);
            }}
          >
            Select all
          </Checkbox>

          {props.selectedRows.length > 0 && (
            <BulkActionsMenu
              handleBulkActionModalState={setBulkActionModalState}
              path={props.path}
              selectedRows={props.selectedRows}
              setSelectedRows={props.setSelectedRows}
              data={props.data}
              onClickSendActivationInvitation={props.onClickSendActivationInvitation}
              onClickSendActivationReminder={props.onClickSendActivationReminder}
            />
          )}
        </div>
      </React.Fragment>
    );

  return (
    <React.Fragment>
      <div className={styles.peopleActions}>
        <div>
          <Searchfield loading={fetchState[props.path].status === FetchStatus.Active && fetchState[props.path].type === FetchType.Custom} onInput={onInputHandler} />
        </div>

        <div>
          {showIconButton ? (
            <Tooltip content="Bulk Upload">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => props.setBulkUploadVisible({ show: true, data: undefined })}>
                <UploadTwoToneIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button type="button" square theme="dark" onClick={() => props.setBulkUploadVisible({ show: true, data: undefined })}>
              <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                <UploadTwoToneIcon />
                Bulk Upload
              </div>
            </Button>
          )}
        </div>

        <div>
          {showIconButton ? (
            <Tooltip content="Add A Person">
              <IconButton iconType="fill" borderColour="borders" backgroundColour="cards" borderRadius="10px" borderWidth={1} onClick={() => props.setPersonToManage({ show: true })}>
                <AddTwoToneIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button type="button" square theme="dark" onClick={() => props.setPersonToManage({ show: true })}>
              <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                <AddTwoToneIcon />
                Add A Person
              </div>
            </Button>
          )}
        </div>

        <div>
          <BulkActionsMenu
            handleBulkActionModalState={setBulkActionModalState}
            path={props.path}
            selectedRows={props.selectedRows}
            setSelectedRows={props.setSelectedRows}
            data={props.data}
            onClickSendActivationInvitation={props.onClickSendActivationInvitation}
            onClickSendActivationReminder={props.onClickSendActivationReminder}
          />
        </div>
      </div>

      <Modal open={bulkActionModalState.showModal} onClose={() => setBulkActionModalState({ selectedPeople: undefined, showModal: false })}>
        <ConfirmationModalLayout
          textAlign="left"
          titleSize="standard"
          onClickCancel={() => setBulkActionModalState({ selectedPeople: undefined, showModal: false })}
          onClickSubmit={() => {
            const { actionType, selectedPeople } = bulkActionModalState;
            if (selectedPeople && (actionType === 'archive' || actionType === 'unarchive')) {
              actions.displayPaywall('archive user');
            }
          }}
          title={bulkActionModalState.actionType === 'archive' ? 'Archive users' : 'Unarchive users'}
          subtitle={`Are you sure you want to ${bulkActionModalState.actionType === 'archive' ? 'archive' : 'unarchive'} ${bulkActionModalState.selectedPeople && bulkActionModalState.selectedPeople?.length > 0 && bulkActionModalState?.selectedPeople.length} people?`}
          submitButtonText={`Yes, ${bulkActionModalState.actionType === 'archive' ? 'archive' : 'unarchive'}`}
          cancelButtonText="No, cancel"
        />
      </Modal>
    </React.Fragment>
  );
}
export function generateDropdownOptionsFromEntities(entities: Entity[] | undefined, onClick?: (value: string) => void): DropdownOption[] {
  const options: DropdownOption[] = [];

  entities?.forEach(entity => {
    if (!options.some(option => option.value === entity.slug)) {
      const option: DropdownOption = { label: entity.name, value: entity.slug! };
      if (!!onClick) option.onClick = () => onClick(entity.slug!);
      options.push(option);
    }
  });

  return options;
}
